import React, { useCallback, useEffect } from 'react';
import { useQuery, gql } from '@apollo/client';
import { toast } from 'react-toastify';
import Lottie from 'react-lottie-player';
import cn from 'classnames';

import Tag from 'Components/Tag';
import { ReactComponent as LightImg } from '../../assets/allocationsHistoryEmptyStateLight.svg';
import { ReactComponent as DarkImg } from '../../assets/allocationsHistoryEmptyStateDark.svg';
import formatDate from 'Shared/util/formatDate';
import EmptyPage from 'Components/EmptyPage';
import animationData from 'Shared/spiner_blue.json';
import withErrorBoundary from 'Components/ErrorBoundary/ErrorBoundary';
import { logger } from 'RootShared/utility/ErrorLogger';
import useDocumentTitle from 'Shared/hooks/useDocumentTitle';

export const queryGetAllocations = gql`
  query TabAllocationsQuery @api(name: root) {
    allocation {
      id
      validFrom
      validUntil
      daysLeft
      amount
      reason {
        id
        text
      }
    }
  }
`;

const TabAllocations = () => {
  const { loading, error, data, refetch } = useQuery(queryGetAllocations);

  useDocumentTitle('Vacations - Allocations');

  useEffect(() => {
    refetch();
  }, [refetch]);

  const mapDataToItems = useCallback((data) => {
    return data.map((allocation) => ({
      ...allocation,
      allocationName: {
        title: allocation.reason.text,
      },
      days: {
        title: 'Allocated',
        subtitle: `${allocation.amount} Days`,
      },
      valid: {
        title: 'Valid',
        subtitle: `${formatDate(allocation.validFrom)} - ${formatDate(allocation.validUntil)}`,
      },
      daysLeft: {
        tagProps: {
          text: `${allocation.daysLeft} days left`,
          size: 'big',
          variant: 'green',
        },
      },
    }));
  }, []);

  const reduceItemsToList = useCallback((allocations, columns) => {
    const mapAllocationToRow = (allocation) => {
      return (
        <li className="list-item" key={allocation.id + Math.random()}>
          {columns.map((column) => (
            <div
              className={cn('list-item__col', {
                'list-item__section_allocation': column === 'allocationName',
                'list-item__section_days-allocated': column === 'days',
                'list-item__section_valid': column === 'valid',
                'list-item__section_days-left': column === 'daysLeft',
              })}
              key={allocation.id + Math.random()}
            >
              {allocation[column].title ? (
                <p
                  className={cn({
                    'list-item__title-big': column === 'allocationName',
                    'list-item__title-small': column !== 'allocationName',
                  })}
                >
                  {allocation[column].title}
                </p>
              ) : null}
              {allocation[column].subtitle ? <p className="list-item__text">{allocation[column].subtitle}</p> : null}
              {allocation[column].tagProps ? <Tag className="list-item__tag" {...allocation[column].tagProps} /> : null}
            </div>
          ))}
        </li>
      );
    };

    return (
      <ul className="list">{allocations ? allocations.map((allocation) => mapAllocationToRow(allocation)) : null}</ul>
    );
  }, []);

  const getTabAllocationsComponent = useCallback(
    (data) => {
      const allocationColumns = ['allocationName', 'days', 'valid', 'daysLeft'];

      return (
        <>
          {data.length ? (
            <div className="tab-allocations" data-testid="tab_allocations">
              <div className="container">{reduceItemsToList(mapDataToItems(data), allocationColumns)}</div>
            </div>
          ) : (
            <EmptyPage
              img={JSON.parse(localStorage.getItem('nightMode')) ? <DarkImg /> : <LightImg />}
              title={'No allocations yet'}
              text={'You need to wait a little while you will have allocations. Anticipation makes everything better.'}
            />
          )}
        </>
      );
    },
    [reduceItemsToList, mapDataToItems]
  );

  if (error) {
    logger.error(error);
    toast(error.message, {
      type: toast.TYPE.ERROR,
      autoClose: 3000,
      closeButton: true,
      hideProgressBar: false,
    });
  }

  return (
    <>
      {loading && (
        <Lottie
          data-testid="tab_allocation_loading"
          className="lottie"
          loop={true}
          play={true}
          animationData={animationData}
          width={100}
          style={{ width: 100, height: 100, margin: '0px auto' }}
        />
      )}
      {data && data.allocation && getTabAllocationsComponent(data.allocation)}
    </>
  );
};

export default withErrorBoundary(TabAllocations);
